import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { FileText, Loader2, Upload, X } from "lucide-react";
import { UrgencyPill, Disclaimer } from "@/components/Badges";
import { SensitiveInfoWarning } from "@/components/SensitiveInfoWarning";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

export default function Summarize() {
  const { lang, t } = useLang();
  const { refreshMe } = useAuth();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);

  const submit = async () => {
    setErr("");
    setResult(null);
    if (text.trim().length < 20) { setErr(lang === "es" ? "Pega más del texto del documento (al menos 20 caracteres)." : "Please paste more of the document text (at least 20 characters)."); return; }
    setLoading(true);
    try {
      const res = await api.post("/summarize", { text, language: lang });
      setResult(res.data);
      if (refreshMe) await refreshMe();
    } catch (e) {
      setErr(e.response?.data?.detail || (lang === "es" ? "Algo salió mal. Intenta de nuevo." : "Something went wrong. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const uploadAndSummarize = async (file) => {
    setErr("");
    setResult(null);
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setErr(lang === "es" ? "Archivo demasiado grande (máx. 10 MB)." : "File too large (10 MB max)."); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const upResp = await api.post("/files/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const info = upResp.data;
      setUploadedFile(info);
      if (info.content_type === "application/pdf") {
        setLoading(true);
        const sumResp = await api.post("/summarize/document", null, { params: { file_id: info.id, language: lang } });
        setResult(sumResp.data);
        if (refreshMe) await refreshMe();
      } else {
        setErr(lang === "es" ? "Foto subida. Para fotos, pega o escribe el texto abajo y toca 'Desglósalo'." : "Photo uploaded. For photos, please also paste or type the text below and click Break it down.");
      }
    } catch (e) {
      setErr(e.response?.data?.detail || (lang === "es" ? "Subida fallida. Intenta de nuevo." : "Upload failed. Please try again."));
    } finally {
      setUploading(false);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <FileText className="w-6 h-6 text-[#C85A32]" /> {t("summarize.title")}
        </h1>
        <p className="text-[#4A525A] mt-1">{t("summarize.subtitle")}</p>
        <div className="mt-2"><SensitiveInfoWarning testid="summarize-sensitive-warning" /></div>
      </div>

      <textarea
        rows={10}
        data-testid="summarize-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("summarize.placeholder")}
        className="w-full rounded-2xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-4 text-base bg-white"
      />

      <div className="bg-[#FAF5EC] border-2 border-dashed border-[#E4DDD3] hover:border-[#C85A32] rounded-2xl p-5 transition-colors">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white border border-[#E4DDD3] flex items-center justify-center text-[#C85A32]">
              <Upload className="w-5 h-5" />
            </div>
            <div>
              <div className="font-bold text-[#1C2024]">{t("summarize.upload_title")}</div>
              <div className="text-xs text-[#4A525A]">{t("summarize.upload_hint")}</div>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
            data-testid="summarize-file-input"
            className="hidden"
            onChange={(e) => uploadAndSummarize(e.target.files?.[0])}
          />
          <button
            type="button"
            data-testid="summarize-upload"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="pod-focus bg-white hover:bg-[#F3EFE6] border-2 border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024] font-bold py-2.5 px-4 rounded-xl inline-flex items-center gap-2"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {t("summarize.choose_file")}
          </button>
        </div>
        {uploadedFile && (
          <div className="mt-3 flex items-center justify-between gap-3 bg-white border border-[#E4DDD3] rounded-xl p-3 text-sm" data-testid="summarize-uploaded">
            <div className="truncate">
              <span className="font-semibold">{uploadedFile.original_filename}</span>
              <span className="text-[#4A525A] ml-2">({Math.round(uploadedFile.size / 1024)} KB)</span>
            </div>
            <button
              onClick={() => setUploadedFile(null)}
              data-testid="summarize-clear-file"
              className="pod-focus text-[#4A525A] hover:text-red-600 p-1 rounded-lg"
              aria-label="Clear file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {err && <div className="text-sm text-red-700" data-testid="summarize-error">{err}</div>}
      <div>
        <button
          onClick={submit}
          disabled={loading}
          data-testid="summarize-submit"
          className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-70 text-white font-bold py-3 px-6 rounded-xl inline-flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {t("summarize.submit")}
        </button>
      </div>

      {result && (
        <div className="space-y-4" data-testid="summarize-result">
          <div className="flex flex-wrap items-center gap-3">
            <UrgencyPill urgency={result.urgency} />
            {result.document_type && (
              <span className="text-xs uppercase tracking-widest bg-[#F1F5F9] text-[#334155] border border-[#475569]/30 px-3 py-1 rounded-full font-bold">
                {result.document_type}
              </span>
            )}
          </div>
          <Card title={t("summarize.one_sentence")} body={result.one_sentence} />
          <Card title={t("summarize.plain")} body={result.plain_summary} />
          <ListCard title={t("summarize.who")} items={result.who_is_involved} />
          <ListCard title={t("summarize.what")} items={result.what_it_says} />
          <ListCard title={t("summarize.dates")} items={result.important_dates} mono />
          <ListCard title={t("summarize.amounts")} items={result.important_amounts} mono />
          <ListCard title={t("summarize.terms")} items={result.legal_terms} />
          <ListCard title={t("summarize.verify")} items={result.things_to_verify} />
          <ListCard title={t("summarize.attorney_questions")} items={result.questions_for_attorney} />
          <Disclaimer>{result.disclaimer}</Disclaimer>
        </div>
      )}
    </div>
  );
}

function Card({ title, body }) {
  if (!body) return null;
  return (
    <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5">
      <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{title}</div>
      <div className="mt-2 text-lg leading-relaxed">{body}</div>
    </div>
  );
}
function ListCard({ title, items, mono }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5">
      <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{title}</div>
      <ul className="mt-3 space-y-2">
        {items.map((it, idx) => (
          <li key={idx} className={`flex gap-3 leading-relaxed ${mono ? "mono" : ""}`}>
            <span className="w-6 h-6 shrink-0 rounded-full bg-[#FAF5EC] text-[#C85A32] font-bold text-sm flex items-center justify-center">{idx + 1}</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
